import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { getCurrentUser } from "@/lib/store";
import { Clock, Coffee, Sun, Moon, Cookie, Wine, Cake, Check, X, Plus, Trash2, Star, Package } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";



const categoryIcons = {
  breakfast: Sun,
  lunch: Coffee,
  dinner: Moon,
  snacks: Cookie,
  beverages: Wine,
  desserts: Cake,
  todays_special: Star,
  combo: Package,
};

const categoryColors = {
  breakfast: "from-orange-400 to-yellow-400",
  lunch: "from-green-400 to-emerald-400",
  dinner: "from-purple-400 to-indigo-400",
  snacks: "from-pink-400 to-rose-400",
  beverages: "from-blue-400 to-cyan-400",
  desserts: "from-red-400 to-orange-400",
  todays_special: "from-[#F4A261] to-[#E76F51]",
  combo: "from-[#8ECAE6] to-[#219EBC]",
};

export default function AdminCategorySettings() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const res = await api.get("/settings");
      const data = res.data || [];
      const currentUser = getCurrentUser();
      if (currentUser?.role === "manager" && currentUser?.property_id) {
        const filtered = data.filter(p => p.property_id === currentUser.property_id);
        setProperties(filtered.length > 0 ? filtered : data);
        setSelectedProperty(currentUser.property_id);
      } else {
        setProperties(data);
        if (data.length > 0) {
          const defaultProp = (currentUser?.property_id && data.some(p => p.property_id === currentUser.property_id))
            ? currentUser.property_id
            : data[0].property_id;
          setSelectedProperty(defaultProp);
        }
      }
    } catch (error) {
      toast.error("Failed to load properties");
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/category-settings/${selectedProperty}`);
      setSettings(res.data);
    } catch (error) {
      toast.error("Failed to load category settings");
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (category, updates) => {
    const currentSetting = settings.find(s => s.category === category);
    const newSetting = { ...currentSetting, ...updates, property_id: selectedProperty };
    
    setSaving(prev => ({ ...prev, [category]: true }));
    try {
      await api.post("/category-settings", newSetting);
      await fetchSettings();
      toast.success(`${category} settings updated`);
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setSaving(prev => ({ ...prev, [category]: false }));
    }
  };

  const handleTimingChange = (category, field, value) => {
    setSettings(prev => prev.map(s => 
      s.category === category 
        ? { ...s, timing: { ...s.timing, [field]: value } }
        : s
    ));
  };

  const addTimeSlot = (category) => {
    setSettings(prev => prev.map(s => {
      if (s.category === category) {
        // Fallback to legacy single timing if time_slots doesn't exist
        const slots = s.timing?.time_slots ? [...s.timing.time_slots] : 
                      (s.timing?.start_time ? [{ start_time: s.timing.start_time, end_time: s.timing.end_time }] : []);
        if (slots.length >= 4) return s;
        return {
          ...s,
          timing: {
            ...s.timing,
            time_slots: [...slots, { start_time: "00:00", end_time: "23:59" }]
          }
        };
      }
      return s;
    }));
  };

  const removeTimeSlot = (category, index) => {
    setSettings(prev => prev.map(s => {
      if (s.category === category) {
        const slots = s.timing?.time_slots || [];
        return {
          ...s,
          timing: {
            ...s.timing,
            time_slots: slots.filter((_, i) => i !== index)
          }
        };
      }
      return s;
    }));
  };

  const handleTimeSlotChange = (category, index, field, value) => {
    setSettings(prev => prev.map(s => {
      if (s.category === category) {
        const slots = [...(s.timing?.time_slots || [])];
        if (slots[index]) {
          slots[index] = { ...slots[index], [field]: value };
        }
        return {
          ...s,
          timing: { ...s.timing, time_slots: slots }
        };
      }
      return s;
    }));
  };

  const saveTiming = (category) => {
    const setting = settings.find(s => s.category === category);
    updateSetting(category, { timing: setting.timing });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-category-settings">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-serif text-[#264653]">Category Timings</h1>
            <p className="text-[#6B705C]">Control when each menu category is available</p>
          </div>
          
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="w-[250px] hm-input" data-testid="property-select">
              <SelectValue placeholder="Select property" />
            </SelectTrigger>
            <SelectContent>
              {properties.map((prop) => (
                <SelectItem key={prop.property_id} value={prop.property_id}>
                  {prop.property_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A9D8F]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {settings.map((setting) => {
              const Icon = categoryIcons[setting.category] || Clock;
              const colorClass = categoryColors[setting.category] || "from-gray-400 to-gray-500";
              
              return (
                <Card key={setting.category} className="overflow-hidden">
                  <CardHeader className={`bg-gradient-to-r ${colorClass} text-white py-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="w-6 h-6" />
                        <CardTitle className="text-lg capitalize">{setting.category === "todays_special" ? "Today's Special" : setting.category}</CardTitle>
                      </div>
                      <div className="flex items-center gap-2">
                        {setting.is_active ? (
                          <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-1 rounded-full">
                            <Check className="w-3 h-3" /> Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs bg-black/20 px-2 py-1 rounded-full">
                            <X className="w-3 h-3" /> Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between">
                      <Label className="text-[#264653] font-medium">Enabled</Label>
                      <Switch
                        checked={setting.is_enabled}
                        onCheckedChange={(checked) => updateSetting(setting.category, { is_enabled: checked })}
                        disabled={saving[setting.category]}
                        data-testid={`toggle-${setting.category}`}
                      />
                    </div>

                    {/* Use Timing Toggle */}
                    <div className="flex items-center justify-between">
                      <Label className="text-[#264653]">Use Time Restrictions</Label>
                      <Switch
                        checked={setting.use_timing}
                        onCheckedChange={(checked) => updateSetting(setting.category, { use_timing: checked })}
                        disabled={saving[setting.category] || !setting.is_enabled}
                        data-testid={`timing-toggle-${setting.category}`}
                      />
                    </div>

                    {/* Timing Inputs */}
                    {setting.use_timing && setting.is_enabled && (
                      <div className="space-y-3 pt-2 border-t">
                        {(() => {
                          // Normalize slots
                          const slots = setting.timing?.time_slots ? setting.timing.time_slots : 
                                        (setting.timing?.start_time ? [{ start_time: setting.timing.start_time, end_time: setting.timing.end_time }] : []);
                          
                          if (slots.length === 0) {
                             // Initialize if empty but use_timing is true
                             slots.push({ start_time: "00:00", end_time: "23:59" });
                          }

                          return (
                            <>
                              {slots.map((slot, idx) => (
                                <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                  <div className="flex-1">
                                    <Label className="text-[10px] uppercase tracking-wider text-gray-500">Start</Label>
                                    <Input
                                      type="time"
                                      value={slot.start_time || "00:00"}
                                      onChange={(e) => handleTimeSlotChange(setting.category, idx, "start_time", e.target.value)}
                                      className="h-8 text-sm px-2 border-gray-200 focus-visible:ring-1"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <Label className="text-[10px] uppercase tracking-wider text-gray-500">End</Label>
                                    <Input
                                      type="time"
                                      value={slot.end_time || "23:59"}
                                      onChange={(e) => handleTimeSlotChange(setting.category, idx, "end_time", e.target.value)}
                                      className="h-8 text-sm px-2 border-gray-200 focus-visible:ring-1"
                                    />
                                  </div>
                                  {slots.length > 1 && (
                                    <Button 
                                      variant="ghost" 
                                      size="icon"
                                      onClick={() => removeTimeSlot(setting.category, idx)}
                                      className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 mt-[18px]"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              
                              {slots.length < 4 && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addTimeSlot(setting.category)}
                                  className="w-full h-8 text-xs border-dashed text-[#2A9D8F] border-[#2A9D8F]/30 hover:bg-[#2A9D8F]/5"
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Add Time Slot
                                </Button>
                              )}
                            </>
                          );
                        })()}
                        <Button
                          size="sm"
                          onClick={() => saveTiming(setting.category)}
                          disabled={saving[setting.category]}
                          className="w-full bg-[#2A9D8F] hover:bg-[#238577] mt-2"
                          data-testid={`save-timing-${setting.category}`}
                        >
                          {saving[setting.category] ? "Saving..." : "Save Timings"}
                        </Button>
                      </div>
                    )}

                    {/* Status Info */}
                    <div className="text-xs text-[#6B705C] pt-2 border-t">
                      {!setting.is_enabled ? (
                        <p>Category is disabled - items won't show to guests</p>
                      ) : setting.use_timing ? (
                        <p>Available during configured time slots</p>
                      ) : (
                        <p>Always available (no time restrictions)</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-[#F8F9FA]">
          <CardContent className="py-6">
            <h3 className="font-semibold text-[#264653] mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#2A9D8F]" />
              How Category Timings Work
            </h3>
            <ul className="space-y-2 text-sm text-[#6B705C]">
              <li>• <strong>Enabled:</strong> Toggle to completely show/hide a category</li>
              <li>• <strong>Time Restrictions:</strong> Items only show during specified hours (IST)</li>
              <li>• <strong>Multi-Category Items:</strong> If an item belongs to multiple categories, it shows if ANY category is active</li>
              <li>• <strong>Example:</strong> Paratha in both Breakfast (7-11 AM) and Dinner (7-11 PM) will show during both windows</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
